import { Center } from '@chakra-ui/react'
import { faTiktok } from '@fortawesome/free-brands-svg-icons'
import {
  faBagsShopping,
  faCirclePlay,
  faFileLines,
  faFilePdf,
  faFileSpreadsheet,
  faImages,
  faNewspaper,
  faWaveformLines,
  faPhotoFilmMusic,
  faPodcast,
} from '@fortawesome/pro-solid-svg-icons'
import _ from 'lodash'

import WhiteLogo from 'src/assets/image/ItlistLogoIconWhiteSVG'
import VoiceTipIcon from 'src/components/widgets/VoiceTipIcon/VoiceTipIcon'
import { BrandColor } from 'src/types'

import { InsideItems } from './Inside'
import AirtableIcon from './InsideCustomIcon/AirtableIcon/AirtableIcon'
import NotionIcon from './InsideCustomIcon/NotionIcon/NotionIcon'
import InsideIcon from './InsideIcon/InsideIcon'

export const getInsideUIData = (
  brandColor: BrandColor,
  creatorName: string,
  creatorImage: string
): InsideItems => ({
  exclusiveContent: {
    label: 'Exclusive resource only found on ItList',
    icon: (
      <Center boxSize="64px" borderRadius="full" bg={brandColor}>
        <WhiteLogo width="35px" />
      </Center>
    ),
    plural: 'Exclusive resources only found on ItList',
    description: ({ creatorName }) =>
      `Exclusive resources could be videos, worksheets, guides, checklists or other valuable resources from ${creatorName} you won't find anywhere else online!`,
  },
  voiceTip: {
    label: `Voice Tip by ${creatorName}`,
    icon: (
      <VoiceTipIcon
        brandColor={brandColor}
        profileImage={creatorImage}
        imageSize={64}
        voiceTipSize={30}
        voiceTipPosition={-4}
        mr="-14px"
      />
    ),
    plural: `Voice Tips by ${creatorName}`,
    description: () =>
      "Creators leave Voice Tips on items inside of ItLists to guide you, share their POV or give additional context as if you're chatting 1-on-1!",
  },
  audio: {
    label: `Audio Recording`,
    icon: <InsideIcon icon={faWaveformLines} brandColor={brandColor} />,
    plural: `Audio Recordings`,
    description: ({ count }) =>
      `${plural(
        count,
        'These',
        'This'
      )} can be audio coaching from ${creatorName}, guided meditations, private podcast recordings or songs. Jump in to listen now!`,
  },
  pdf: {
    label: 'PDF',
    icon: <InsideIcon icon={faFilePdf} brandColor={brandColor} />,
    plural: 'PDFs',
    description: ({ count, creatorNamePossessive }) =>
      `PDFs can be worksheets, workbooks, guides, checklists, presentations etc. Looks like there${plural(
        count,
        ' are',
        "'s"
      )} ${count} of them in ${creatorNamePossessive} ItList!`,
  },
  product: {
    label: 'Product suggestion',
    icon: <InsideIcon icon={faBagsShopping} brandColor={brandColor} />,
    plural: 'Product suggestions',
    description: ({ creatorNamePossessive }) =>
      `Inside you'll find ${creatorNamePossessive} product recs on this topic. Creators can also add their promo codes with discounts you won't find anywhere else!`,
  },
  image: {
    label: 'Image',
    icon: <InsideIcon icon={faImages} brandColor={brandColor} />,
    plural: 'Images',
    description: ({ count }) =>
      `${creatorName} created or found ${plural(
        count,
        'some',
        'a'
      )} helpful photo${plural(count)} or infographic${plural(
        count
      )} that relate${plural(count, '', 's')} to the topic of this ItList.`,
  },
  video: {
    label: 'Key video',
    icon: <InsideIcon icon={faCirclePlay} brandColor={brandColor} />,
    plural: 'Key videos',
    description: ({ count, creatorName }) =>
      `Skip the YouTube rabbit hole and get right to what matters by watching the video${plural(
        count
      )} ${creatorName} included in this ItList.`,
  },
  podcast: {
    label: 'Relevant podcast',
    icon: <InsideIcon icon={faPodcast} brandColor={brandColor} />,
    plural: 'Relevant podcasts',
    description: ({ creatorName }) =>
      `Podcasts are amazing nuggets of info these days but it can be hard to find relevant shows and episodes. ${creatorName} did that legwork for you!`,
  },
  notionDoc: {
    label: 'Notion doc',
    icon: <NotionIcon brandColor={brandColor} />,
    plural: 'Notion docs',
    description: ({ creatorName, count }) =>
      `Notion docs could be templates, databases, guides, notes or other cool Notion things. Looks like ${creatorName} included ${count} of them inside!`,
  },
  spreadsheet: {
    label: 'Spreadsheet',
    icon: <InsideIcon icon={faFileSpreadsheet} brandColor={brandColor} />,
    plural: 'Spreadsheets',
    description: ({ count, creatorName }) =>
      `Get organized or crunch numbers like a pro with the spreadsheet${plural(
        count
      )} ${creatorName} included in this ItList.`,
  },
  airtable: {
    label: 'Airtable',
    icon: <AirtableIcon brandColor={brandColor} />,
    plural: 'Airtables',
    description: ({ count, creatorName }) =>
      `Airtables are like spreadsheets with superpowers. You can organize data, create workflows, or even apps! ${creatorName} included ${count} relevant Airtable${plural(
        count
      )} in this ItList.`,
  },
  wordDoc: {
    label: 'Word doc',
    icon: <InsideIcon icon={faFileLines} brandColor={brandColor} />,
    plural: 'Word docs',
    description: ({ creatorName }) =>
      `What's full of valuable info and will never come up on your Google search? Word Docs! ${creatorName} has you covered.`,
  },
  article: {
    label: 'Relevant article',
    icon: <InsideIcon icon={faNewspaper} brandColor={brandColor} />,
    plural: 'Relevant articles',
    description: ({ count, creatorName }) =>
      `${creatorName} hand-picked ${plural(
        count,
        'articles',
        'an article'
      )} for you as high quality content on this topic. Jump in to read now or bookmark for later!`,
  },
  social: {
    label: 'Post on this topic',
    icon: <InsideIcon icon={faTiktok} brandColor={brandColor} />,
    plural: 'Posts on this topic',
    description: ({ creatorName }) =>
      `Social platforms are more concerned with showing you ads than helping you find important info. ${creatorName} linked all relevant posts directly to this ItList - guiding you straight to what actually matters!`,
  },
  other: {
    label: 'Other item',
    icon: <InsideIcon icon={faPhotoFilmMusic} brandColor={brandColor} />,
    plural: 'Other items',
    description: ({ creatorName }) =>
      `A mystery box! View this ItList to see what else ${creatorName} hand-picked for you inside!`,
  },
})

function plural(count: number, pluralSuffix = 's', singularSuffix = '') {
  return `${count === 1 ? singularSuffix : pluralSuffix}`
}
